import { crmText as translateCrmCopy } from '../../ui/locale/crmLocale';
import { Component, Suspense } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import React from 'react';
import { resolveAppBasename, resolveRoutePolicy, toRouterPath } from '../../navigation/routePolicy';
import { recordNavigationTelemetry } from '../../navigation/telemetry';

// Lazy-load the InternalServerError page to avoid conflicting static + dynamic imports
const InternalServerError = React.lazy(() => import('./InternalServerError'));

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree and displays a fallback UI
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const basename = resolveAppBasename(window.location.pathname);
    const policy = resolveRoutePolicy({
      basename,
      pathname: toRouterPath(basename, window.location.pathname),
    });
    recordNavigationTelemetry({
      name: 'reconstruction_failed',
      ...(policy ? { routeId: policy.id, shell: policy.shell } : {}),
      reason: 'render-error',
    });

    // Log error to console in development
    if (import.meta.env?.DEV) {
      console.error('Error caught by boundary:', error);
      console.error('Error info:', errorInfo);
    }
    
    // In production, you could send error to monitoring service here
    // Example: logErrorToService(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided, otherwise show generic error page
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      return (
        <Suspense fallback={<div className="p-10 text-center text-slate-500">{translateCrmCopy("Loading…")}</div>}>
          <InternalServerError />
        </Suspense>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
